import React, { useState } from 'react';
import { FaPlay } from 'react-icons/fa6';
import { ColumnsType } from 'antd/es/table';
import { toast } from 'react-toastify';

import {
  useGetSegmentationLabelsQuery,
  useGetSegmentationReferencesQuery,
  useGetKeypointLabelsQuery,
  useGetPerspectivesQuery,
} from '../../../services/dicomService';

import {
  SegmentsContainer,
  HeaderRow,
  SegmentsLabel,
  ExpandIcon,
  StyledTable,
  TabButton,
  ClickableUUID,
} from './styled';

import { Perspective, ApiMediaData, KeypointLabel, KeypointCollectionLabel } from 'types/labels';

export interface SegmentationPayload {
  // maskUrl removed - we will use vector `paths` only for segmentations/references
  maskIcid: string;
  frameUrl: string;
  frameIcid: string;
  frameExtension: string;
  paths?: string[];
}

export interface SegmentsProps {
  mediaUuid: string;
  dicomId: number;
  onSegmentationClick: (payload: SegmentationPayload) => void;
  onKeypointClick?: (payload: {
    frameUrl: string;
    frameIcid: string;
    frameExtension: string;
    keypoints: KeypointLabel[];
  }) => void;
  /** Start expanded (default true). Nothing is fetched while collapsed. */
  defaultExpanded?: boolean;
}

const PlayIcon: React.FC<{ size?: number }> = props => {
  const Icon = FaPlay as React.ComponentType<{ size?: number }>;
  return <Icon {...props} />;
};

const Segments: React.FC<SegmentsProps> = ({
  mediaUuid,
  dicomId,
  onSegmentationClick,
  onKeypointClick,
  defaultExpanded = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [activeTab, setActiveTab] = useState<
    'segmentations' | 'keypoints' | 'perspectives' | 'references'
  >('segmentations');

  const { data: segmentationsData = [], isFetching: isSegFetching } = useGetSegmentationLabelsQuery(
    mediaUuid,
    { 
      // Only fetch if mediaUuid exists AND we are on the segmentations tab
      skip: !isExpanded || !mediaUuid || activeTab !== 'segmentations'
    }
  );

  const { data: keypointCollectionData = [], isFetching: isKeypointFetching } =
    useGetKeypointLabelsQuery(mediaUuid, { 
      skip: !isExpanded || !mediaUuid || activeTab !== 'keypoints'
    });

  const { data: perspectivesData = [], isFetching: isPersFetching } = useGetPerspectivesQuery(
    dicomId,
    { 
      skip: !isExpanded || !dicomId || activeTab !== 'perspectives'
    }
  );

  const { data: segRefsData = [], isFetching: isSegRefsFetching } =
    useGetSegmentationReferencesQuery(mediaUuid, { 
      skip: !isExpanded || !mediaUuid || activeTab !== 'references'
    });

  const playColumn = {
    title: '',
    key: 'play',
    render: () => (
      <button style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
        <span
          style={{
            transform: 'rotate(90deg)',
            backgroundColor: 'grey',
            padding: '2px',
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
          }}
        >
          <PlayIcon size={16} />
        </span>
      </button>
    ),
  };

  const columnsSegmentations: ColumnsType<ApiMediaData> = [
    playColumn,
    {
      title: 'Icid',
      dataIndex: 'icid',
      key: 'icid',
      render: (_: string, record) => (
        <ClickableUUID
          onClick={() => {
            if (!(record.paths && record.paths.length)) {
              toast.error('Segmentation path data does not exist.', {
                position: 'top-right',
                autoClose: 3000,
              });
              return;
            }
            onSegmentationClick({
              maskIcid: record.icid,
              frameUrl: record.frame?.signed_url ?? '',
              frameIcid: record.frame?.icid ?? '',
              frameExtension: record.frame?.extension ?? 'png',
              paths: record.paths,
            });
          }}
        >
          {record.icid}
        </ClickableUUID>
      ),
    },
    {
      title: 'Technician',
      key: 'user',
      render: (_: any, record) => record.user?.first_name ?? '—',
    },
    {
      title: 'Feature',
      key: 'feature',
      render: (_: any, record) => record.segmentation_class?.value ?? '—',
    },
    {
      title: 'Frame #',
      key: 'frame',
      render: (_: any, record) => record.frame?.index ?? '',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d: string) => new Date(d).toLocaleString(),
    },
  ];

  const columnsReferences: ColumnsType<any> = [
    playColumn,
    {
      title: 'Icid',
      dataIndex: 'icid',
      key: 'icid',
      render: (_: string, record) => (
        <ClickableUUID
          onClick={() => {
            if (!(record.paths && record.paths.length)) {
              toast.error('Segmentation reference path data does not exist.', {
                position: 'top-right',
                autoClose: 3000,
              });
              return;
            }
            onSegmentationClick({
              maskIcid: record.icid,
              frameUrl: record.frame?.signed_url ?? '',
              frameIcid: record.frame?.icid ?? '',
              frameExtension: record.frame?.extension ?? 'png',
              paths: record.paths,
            });
          }}
        >
          {record.icid}
        </ClickableUUID>
      ),
    },
    {
      title: 'Feature',
      key: 'feature',
      render: (_: any, record) => record.segmentation_class?.value ?? '—',
    },
    {
      title: 'Frame #',
      key: 'frame',
      render: (_: any, record) => record.frame?.index ?? '',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d: string) => new Date(d).toLocaleString(),
    },
  ];

  const columnsKeypointCollections: ColumnsType<KeypointCollectionLabel> = [
    {
      title: 'ICID',
      dataIndex: 'icid',
      key: 'icid',
      render: (_: any, record) => (
        <ClickableUUID
          onClick={() => {
            if (!record.frame?.signed_url) {
              toast.error('Frame file does not exist.', { position: 'top-right', autoClose: 3000 });
              return;
            }
            if (typeof onKeypointClick === 'function') {
              const frameKeypoints = (record.keypoint_labels || []).map(kp => ({
                ...kp,
                frame: record.frame,
                keypoint_collection_icid: record.icid,
              }));
              onKeypointClick({
                frameUrl: record.frame.signed_url,
                frameIcid: record.frame.icid,
                frameExtension: 'png',
                keypoints: frameKeypoints,
              });
            }
          }}
        >
          {record.icid}
        </ClickableUUID>
      ),
    },
    {
      title: 'Frame ICID',
      key: 'frame_icid',
      render: (_: any, record) => record.frame?.icid ?? '—',
    },
    {
      title: 'Technician',
      key: 'user',
      render: (_: any, record) => {
        const user = record.user;
        if (user?.first_name && user?.last_name) {
          return `${user.first_name} ${user.last_name}`;
        } else if (user?.first_name) {
          return user.first_name;
        } else if (user?.last_name) {
          return user.last_name;
        }
        return '—';
      },
    },
    {
      title: 'Keypoint Collection Class',
      key: 'keypoint_collection_class',
      render: (_: any, record) => record.keypoint_collection_class?.value ?? '—',
    },
  ];

  const columnsPerspectives: ColumnsType<Perspective> = [
    playColumn,
    {
      title: 'Icid',
      dataIndex: 'icid',
      key: 'icid',
      render: (_: unknown, record: Perspective) => {
        const icid = record.view_label?.icid ?? record.quality_label?.icid ?? '—';
        return <ClickableUUID>{icid}</ClickableUUID>;
      },
    },
    {
      title: 'Quality',
      key: 'quality_label',
      render: (_: any, rec: any) => rec.quality_label?.quality_class ?? '—',
    },
    {
      title: 'User',
      key: 'user',
      render: (_: any, rec: any) => rec.user?.first_name ?? '—',
    },
    {
      title: 'View',
      key: 'view_class',
      render: (_: any, rec: any) => rec.view_class?.view ?? '—',
    },
  ];

  return (
    <SegmentsContainer>
      <HeaderRow>
        <SegmentsLabel onClick={() => setIsExpanded(!isExpanded)}>
          <ExpandIcon>{isExpanded ? '▲' : '▼'}</ExpandIcon>
          Labels
        </SegmentsLabel>
        
        {/* Buttons now effectively act as the "Trigger" for the fetch */}
        <TabButton
          active={activeTab === 'perspectives'}
          onClick={() => {
            setActiveTab('perspectives');
            setIsExpanded(true);
          }}
        >
          Perspectives
        </TabButton>
        <TabButton
          active={activeTab === 'segmentations'}
          onClick={() => {
            setActiveTab('segmentations');
            setIsExpanded(true);
          }}
        >
          Segmentations
        </TabButton>
        <TabButton
          active={activeTab === 'keypoints'}
          onClick={() => {
            setActiveTab('keypoints');
            setIsExpanded(true);
          }}
        >
          Keypoints
        </TabButton>
        <TabButton
          active={activeTab === 'references'}
          onClick={() => {
            setActiveTab('references');
            setIsExpanded(true);
          }}
        >
          Segmentation References
        </TabButton>
      </HeaderRow>
      {isExpanded && (
        <>
          <StyledTable<any>
            columns={
              activeTab === 'keypoints'
                ? columnsKeypointCollections
                : activeTab === 'perspectives'
                  ? columnsPerspectives
                  : activeTab === 'references'
                    ? columnsReferences
                    : columnsSegmentations
            }
            dataSource={
              activeTab === 'keypoints'
                ? keypointCollectionData
                : activeTab === 'perspectives'
                  ? perspectivesData
                  : activeTab === 'references'
                    ? segRefsData
                    : segmentationsData
            }
            rowClassName={(record: any) => {
              if (activeTab === 'segmentations' || activeTab === 'references') {
                if (!(record.paths && record.paths.length)) {
                  return 'missing-paths-row';
                }
              }
              return '';
            }}
            rowKey={(record: any, index: number) =>
              record.view_label?.uuid ?? record.quality_label?.uuid ?? `perspective-${index}`
            }
            loading={
              activeTab === 'keypoints'
                ? isKeypointFetching
                : activeTab === 'perspectives'
                  ? isPersFetching
                  : activeTab === 'references'
                    ? isSegRefsFetching
                    : isSegFetching
            }
            pagination={false}
            size="small"
          />
        </>
      )}
    </SegmentsContainer>
  );
};

export default Segments;